import dayjs from 'dayjs'
import { Button, Modal, Table } from "antd"
import { useCompletedWorkareaVisitsQuery, useUpdateCompletedVisitPricingMutation } from "../../../../../domain/invoicing/invoicingQueries"
import { hasValue } from "../../../../../helpers/nullable"
import { useContext, useEffect, useState } from "react"
import { centsToDollarsDisplay } from "../../../../displayFormatters"
import { ItemsContext } from "../../../../Items/ItemsRoot"
import { getBillablePerVisitAmounts } from "../../../../invoicing/Creation/createInvoiceModel"
import { pluralize } from '../../../../../helpers/strings'
import VSpace from '../../../../common/VSpace'

type Props = {
  jobId: number
  workareaId: number
  currentPrice?: number
}

const PerVisitPriceUpdater = ({jobId, workareaId, currentPrice}: Props) => {
  const { jobData: { visitData } } = useContext(ItemsContext)
  const completedWorkareaVisitsQuery = useCompletedWorkareaVisitsQuery(jobId, hasValue(currentPrice))
  const updateCompletedVisitPricingMutation = useUpdateCompletedVisitPricingMutation(() => setIsModalOpen(false))
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedVisitIds, setSelectedVisitIds] = useState<string[]>([])
  const candidates = completedWorkareaVisitsQuery.data?.filter(x => x.workareaId === workareaId && !x.isInvoiced && x.visitPrice !== currentPrice) ?? []
  const selections = getBillablePerVisitAmounts(visitData, candidates)

  useEffect(() => {
    if (completedWorkareaVisitsQuery.data && hasValue(currentPrice)) {
      setSelectedVisitIds(selections.map(x => x.visitId))
    }
  }, [completedWorkareaVisitsQuery.data, currentPrice])

  if (!hasValue(currentPrice)) {
    return null
  }
  
  if (candidates.length === 0) {
    return null
  }

  return <div>
    <Button onClick={() => setIsModalOpen(true)} size="large">Edit Past Visit Prices</Button>
    <Modal
      title="Edit Past Visit Prices"
      open={isModalOpen}
      okText={`Update ${selectedVisitIds.length} ${pluralize(selectedVisitIds.length, 'visit')} to ${centsToDollarsDisplay(currentPrice)}`}
      okButtonProps={{
        disabled: selectedVisitIds.length === 0 || updateCompletedVisitPricingMutation.isLoading,
        loading: updateCompletedVisitPricingMutation.isLoading
      }}
      onOk={() => updateCompletedVisitPricingMutation.mutate({
        jobId,
        workareaId,
        visitIds: selectedVisitIds,
        visitPrice: currentPrice
      })}
      onCancel={() => setIsModalOpen(false)}
    >
      <VSpace>
        <div>The following completed visits have not been invoiced and do not match the current per-visit price of {centsToDollarsDisplay(currentPrice)}.</div>
        <Table
          size="small"
          dataSource={selections}
          columns={[
            {
              title: 'Visit Date',
              render: (_, x) => dayjs(x.date).format('MMM D, YYYY')
            },
            {
              title: 'Price',
              render: (_, x) => centsToDollarsDisplay(x.visitPrice)
            }
          ]}
          rowKey="visitId"
          rowSelection={{
            selectedRowKeys: selectedVisitIds,
            onChange: (keys) => setSelectedVisitIds(keys as string[])
          }}
          pagination={{
            hideOnSinglePage: true,
          }}
        />
      </VSpace>
    </Modal>
  </div>
}

export default PerVisitPriceUpdater
